import { Show } from "solid-js";
import { CoinsStrip } from "./coins_strip";
import { useDenominationsGridV2Context } from "../context/denominations_grid_v2_context";
import { useTelescopeContext } from "@hubble/telescope";
import { toRupees } from "~/utils/number";
import { coinIcon } from "~/assets/assets";
import { openModalV2 } from "~/shared_states/modal";
import { CoinsConfirmationModal } from "./coins_confirmation_modal";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { clientIdConfig } from "~/data/partner_config";
import { useWebStoreContext } from "~/context/webstore_context";

export function GcAmountFooter() {
  const gridState = useDenominationsGridV2Context();
  const { components } = useTelescopeContext();
  const analyticsService = useAnalyticsService();
  const { getClientId } = useWebStoreContext();
  const { internalState, externalProps, enoughCoinsAvailable } = gridState;
  const {
    selectedAmount,
    selectedVariantId,
    useCoins,
    ctaState,
    simulationStore,
    getVouchersCount,
    getCoinsDiscountAmount,
  } = internalState;
  const {
    isCoinsOnly,
    initiateOrder,
    allowRewardCoins,
    coinsBalance,
    isRewardTypePostPaid,
    brand,
  } = externalProps;

  const isPaymentCtaEnabled = () => {
    return (
      ctaState() === "DEFAULT" &&
      selectedAmount() != null &&
      simulationStore.error == null &&
      enoughCoinsAvailable()
    );
  };

  const _initiateOrder = () => {
    initiateOrder({
      amount: selectedAmount(),
      variantId: selectedVariantId(),
      consumeCoins: useCoins(),
      denominationDetails: internalState.getDenominationDetails(),
    });
  };

  const showConfirmationModal = () => {
    openModalV2(
      () => (
        <CoinsConfirmationModal
          coinAmount={getCoinsDiscountAmount()}
          currentBalance={coinsBalance}
          brandLogoUrl={brand.voucherProductMetadata.sqLogoUrl}
          brandTitle={brand.voucherProductMetadata.title}
          onConfirm={_initiateOrder}
        />
      ),
      {
        size: "small",
      }
    );
  };

  const handleCtaClick = () => {
    if (!isPaymentCtaEnabled()) return;

    analyticsService.logEvent(HubbleEvent.CLICK_GET_THIS_CARD, {
      amount: selectedAmount(),
      brandName: brand.name,
    });

    // Show confirmation modal for 0 due amount (coins-only) without offer construct
    if (simulationStore.response?.dueAmount == 0) {
      showConfirmationModal();
    } else {
      _initiateOrder();
    }
  };

  const getCtaText = () => {
    if (getClientId() === clientIdConfig.onecardClientId) {
      return "Get this GiftCard";
    }
    return getVouchersCount() > 1 ? "Get cards" : "Get this card";
  };

  const discountPercentage = () => {
    return (
      simulationStore.response?.discountDetails?.totalDiscount?.percentage ?? 0
    );
  };

  const redemptionAmount = () => {
    return simulationStore.response?.redemptionAmount ?? selectedAmount();
  };

  const PaymentInfoValidated = () => {
    return (
      <>
        {/* Coins strip for coins-only flow */}
        <Show when={isCoinsOnly}>
          <CoinsStrip showLowBalanceWarning={true} />
        </Show>

        {/* Toggle for reward coins */}
        <Show when={allowRewardCoins && coinsBalance > 0 && !isCoinsOnly}>
          <div class="flex h-9 w-full items-center justify-between rounded-lg bg-background-normal-secondary px-4 py-2">
            <div class="flex flex-1 items-center gap-2">
              <img src={coinIcon} alt="coin" class="h-5 w-5" />
              <div class="flex flex-1 items-center gap-1 text-para-3-regular tracking-[-0.14px]">
                <Show
                  when={getCoinsDiscountAmount() == 0}
                  fallback={
                    <>
                      <span class="text-para-3-medium text-text-normal-primary">
                        Using {getCoinsDiscountAmount()} coins
                      </span>
                    </>
                  }
                >
                  <span class="text-para-3-medium text-text-normal-primary">
                    Use coins
                  </span>
                  <span class="text-text-normal-secondary">·</span>
                  <span class="text-text-normal-secondary">
                    Balance ₹{coinsBalance.toLocaleString()}
                  </span>
                </Show>
              </div>
            </div>
            <components.Toggle
              checked={useCoins()}
              onChange={(checked) => {
                internalState.setUseCoins(checked);
              }}
            />
          </div>
        </Show>

        {/* Payment info for non-coins-only flow */}
        <Show when={!isCoinsOnly && !isRewardTypePostPaid}>
          <div
            class="flex w-full items-start gap-2 transition-opacity duration-300"
            classList={{
              "opacity-0": simulationStore.error != null,
            }}
          >
            <div class="flex flex-1 flex-col">
              <div class="text-para-3-medium tracking-[-0.14px] text-text-normal-primary">
                You pay
              </div>
              <div class="flex items-end gap-[5px]">
                <div
                  class="text-title-4-semi-bold tracking-[-0.3px]"
                  classList={{
                    "text-text-normal-primary": discountPercentage() === 0,
                    "text-success-base": discountPercentage() > 0,
                  }}
                >
                  {toRupees(simulationStore.response?.dueAmount ?? 0)}
                </div>
                <Show when={discountPercentage() > 0}>
                  <div class="flex h-full items-end">
                    <div class="text-para-3-regular tracking-[-0.14px] text-text-normal-secondary line-through">
                      {toRupees(redemptionAmount())}
                    </div>
                  </div>
                </Show>
              </div>
            </div>
            <Show when={discountPercentage() > 0}>
              <div class="flex h-12 flex-col items-center justify-end py-[2px]">
                <div class="text-para-3-medium tracking-[-0.14px] text-success-base">
                  Instant {discountPercentage()}% off
                </div>
              </div>
            </Show>
          </div>
        </Show>

        {/* Multiple cards info */}
        <Show when={getVouchersCount() > 1 && simulationStore.error == null}>
          <div class="rounded-lg bg-info-lighter px-4 py-2 text-center text-title-6-medium tracking-[-0.14px] text-info-dark">
            You'll get {getVouchersCount()} gift cards totalling{" "}
            {toRupees(redemptionAmount())}
          </div>
        </Show>
      </>
    );
  };

  return (
    <div class="flex w-full flex-col gap-3">
      {/* Divider */}
      <div class="h-px w-full bg-stroke-1" />
      <Show when={simulationStore.error == null}>
        {/* all payemnt related info is hidden in case of error */}
        <PaymentInfoValidated />
      </Show>

      {/* CTA Button */}
      <components.Button
        text={getCtaText()}
        variant="primary"
        style="neutral"
        size={40}
        expanded={true}
        disabled={!isPaymentCtaEnabled()}
        loading={ctaState() === "LOADING" || simulationStore.isSimulating}
        onClick={handleCtaClick}
        TrailIcon={() => <ArrowRightIcon />}
      />
    </div>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M19 12H5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14 17L19 12"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14 7L19 12"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
